import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';

export interface Calc1State {
  leadsMes: number;
  atendido5min: number;
  ticketMedio: number;
  conversaoAtual: number;
  conversaoPotencial: number;
}

export interface Calc2State {
  reunioesMes: number;
  propostaPct: number;
  vendaAtualPct: number;
  vendaPotencialPct: number;
  ticketMedio: number;
}

export interface Calc3State {
  clientesAtivos: number;
  recompraAtualPct: number;
  recompraPotencialPct: number;
  ticketRecompra: number;
}

export interface Calc4State {
  custoSaasMes: number;
  horasRepetitivasMes: number;
  custoHora: number;
  otimizavelPct: number;
}

export const DEFAULTS = {
  c1: {
    leadsMes: 200,
    atendido5min: 20,
    ticketMedio: 5000,
    conversaoAtual: 12,
    conversaoPotencial: 30,
  } as Calc1State,
  c2: {
    reunioesMes: 40,
    propostaPct: 60,
    vendaAtualPct: 25,
    vendaPotencialPct: 40,
    ticketMedio: 5000,
  } as Calc2State,
  c3: {
    clientesAtivos: 300,
    recompraAtualPct: 20,
    recompraPotencialPct: 45,
    ticketRecompra: 3000,
  } as Calc3State,
  c4: {
    custoSaasMes: 4500,
    horasRepetitivasMes: 80,
    custoHora: 60,
    otimizavelPct: 50,
  } as Calc4State,
};

export function calc1Result(s: Calc1State) {
  const delta = Math.max(0, s.conversaoPotencial - s.conversaoAtual);
  const mes = s.leadsMes * (delta / 100) * s.ticketMedio;
  return { mes, ano: mes * 12 };
}

export function calc2Result(s: Calc2State) {
  const propostas = s.reunioesMes * (s.propostaPct / 100);
  const atual = propostas * (s.vendaAtualPct / 100);
  const potencial = propostas * (s.vendaPotencialPct / 100);
  const mes = Math.max(0, potencial - atual) * s.ticketMedio;
  return { mes, ano: mes * 12 };
}

export function calc3Result(s: Calc3State) {
  const atual = s.clientesAtivos * (s.recompraAtualPct / 100);
  const potencial = s.clientesAtivos * (s.recompraPotencialPct / 100);
  const ano = Math.max(0, potencial - atual) * s.ticketRecompra;
  return { ano, mes: ano / 12 };
}

export function calc4Result(s: Calc4State) {
  const custoHorasMes = s.horasRepetitivasMes * s.custoHora;
  const totalMes = s.custoSaasMes + custoHorasMes;
  const mes = totalMes * (s.otimizavelPct / 100);
  return { mes, ano: mes * 12 };
}

interface CalculatorContextValue {
  c1: Calc1State;
  c2: Calc2State;
  c3: Calc3State;
  c4: Calc4State;
  setC1: (s: Calc1State | ((p: Calc1State) => Calc1State)) => void;
  setC2: (s: Calc2State | ((p: Calc2State) => Calc2State)) => void;
  setC3: (s: Calc3State | ((p: Calc3State) => Calc3State)) => void;
  setC4: (s: Calc4State | ((p: Calc4State) => Calc4State)) => void;
  totals: {
    receitaPerdidaAno: number;
    margemPerdidaAno: number;
    lucroPerdidoAno: number;
    custoOtimizavelAno: number;
    totalAno: number;
  };
  reset: () => void;
}

const Ctx = createContext<CalculatorContextValue | null>(null);

export function CalculatorProvider({ children }: { children: ReactNode }) {
  const [c1, setC1] = useLocalStorage<Calc1State>('rai_c1', DEFAULTS.c1);
  const [c2, setC2] = useLocalStorage<Calc2State>('rai_c2', DEFAULTS.c2);
  const [c3, setC3] = useLocalStorage<Calc3State>('rai_c3', DEFAULTS.c3);
  const [c4, setC4] = useLocalStorage<Calc4State>('rai_c4', DEFAULTS.c4);

  const totals = useMemo(() => {
    const r1 = calc1Result(c1).ano;
    const r2 = calc2Result(c2).ano;
    const r3 = calc3Result(c3).ano;
    const r4 = calc4Result(c4).ano;
    return {
      receitaPerdidaAno: r1,
      margemPerdidaAno: r2,
      lucroPerdidoAno: r3,
      custoOtimizavelAno: r4,
      totalAno: r1 + r2 + r3 + r4,
    };
  }, [c1, c2, c3, c4]);

  const reset = useCallback(() => {
    setC1(DEFAULTS.c1);
    setC2(DEFAULTS.c2);
    setC3(DEFAULTS.c3);
    setC4(DEFAULTS.c4);
  }, [setC1, setC2, setC3, setC4]);

  const value = useMemo(
    () => ({ c1, c2, c3, c4, setC1, setC2, setC3, setC4, totals, reset }),
    [c1, c2, c3, c4, setC1, setC2, setC3, setC4, totals, reset]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCalculators() {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error('useCalculators precisa estar dentro de CalculatorProvider');
  return ctx;
}
